import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import type { IntegrityEventKind, InterviewMode } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import {
  FACE_SAMPLE_MS,
  createFaceSource,
  createPresenceMachine,
  noticeFor,
  observeFaces,
} from "./faceDetection.js";
import type { CameraNotice, PresenceMachine } from "./faceDetection.js";
import { createGazeFaceSource, createGazeMachine, observeGaze } from "./gazeDetection.js";
import type { GazeMachine, GazeStatus } from "./gazeDetection.js";

/**
 * Candidate · browser-integrity proctoring. Captures advisory integrity SIGNALS during the live
 * interview — switching tab/window, pasting into the answer box, leaving fullscreen, and, where a
 * local face detector exists, nobody or more than one person in the camera frame — and ships them
 * in batches to the API for the HIRING TEAM to review. These are FLAGS for a human, never an
 * automated decision and never a "personality" or confidence score. Everything here is strictly
 * best-effort: it never throws, never blocks and never interrupts the interview, so a flaky sensor
 * or a failed request can't trap or penalise the candidate.
 *
 * CAMERA PRESENCE (this is the part that was claimed but not built). The detection itself lives in
 * faceDetection.ts — browser `FaceDetector` where it exists, otherwise a self-hosted MediaPipe
 * model, otherwise nothing. All of it is on-device: we pay nothing for vision, exactly as we pay
 * nothing for speech, and no frame ever leaves the candidate's machine. What this hook adds on top:
 *
 *   • HEARTBEATS, so silence is readable. `proctor_on` says the integrity channel came up at all;
 *     `face_watch_on` is emitted only after the detector returns a REAL reading from a REAL frame.
 *     Without them the scorer cannot tell "we watched and saw nothing wrong" from "we never
 *     watched", and it used to pay both as near-perfect (see computePresence).
 *   • A CAMERA GATE. Face sampling starts on `cameraActive`, NOT on the interview being live. The
 *     two are not the same thing and conflating them is what let a candidate who DENIED the camera
 *     be sampled against a blank <video>, read as "0 faces", and flagged for hiding. No live
 *     camera → no detector is even downloaded, no reading is taken, no event is emitted, and the
 *     report says plainly that camera presence was not measured.
 *   • DEBOUNCE, so glances don't become flags. One event per sustained episode, several seconds
 *     of consecutive readings deep — never one per poll.
 *   • A RECEIPT on the way back (`face_visible`, carrying the duration), so a report shows how
 *     long someone was out of frame instead of just how many times.
 *   • A KIND WORD to the candidate — `cameraNotice`, rendered by InterviewStep at the TOP of the
 *     self-view column. It never blocks, never pauses, never accuses. This is the ONLY mechanism
 *     by which a candidate can fix an absence before it reaches a recruiter, and it once existed
 *     here in full — debounce, copy, twelve tests — while never being mounted on screen at all,
 *     which made the whole feature a report about people we never told. If you change this
 *     contract, change the render with it: `grep -rn cameraNotice apps/web/src` must hit
 *     InterviewStep.tsx.
 *     It is non-null ONLY while the problem is live, which means only while the candidate is
 *     looking away from the screen — so the surface keeps it legible for a few seconds after
 *     recovery (cameraNoticeView.ts). Don't "simplify" that back into a bare render of this
 *     field: a notice nobody is present to read is the same as no notice.
 *
 * No detector → no events, no notice, nothing in the report claiming we looked. Honest silence is
 * the only acceptable failure mode; a fabricated "all clear" would be worse than no feature.
 */

// The kind union comes from the shared contract, not a local copy: the API validator and the
// presence scorer read the same list, and a name only this file knew about is exactly how three
// of these signals once ended up stored but never scored.
interface IntegrityEvent {
  kind: IntegrityEventKind;
  at: string;
  detail?: string;
}

// A React paste event is structurally just something carrying a clipboardData; typing it loosely
// keeps the handler attachable to any textarea without dragging in the React event namespace.
type PasteLike = { clipboardData: DataTransfer | null };

const FLUSH_MS = 10_000; // batch cadence — quiet enough to not chatter, frequent enough to survive a crash
const MAX_QUEUE = 500; // mirrors integrityBatchSchema.events.max(500) on the API — never grow unbounded

/** The strict-enforcement events InterviewStep owns the lifecycle of (pause/resume/bypass). The
 *  union is deliberately this narrow: the interview surface gets to timestamp its own strictness
 *  timeline, not to invent new vocabulary — everything else still flows through this hook. */
export type StrictEventKind = Extract<IntegrityEventKind, "strict_pause" | "strict_resume" | "strict_bypass">;

/** What one sampler tick saw, whichever detector produced it. Natural sessions carry gaze: null
 *  always; strict sessions carry the landmarker's estimate (itself null when faces !== 1). */
interface SampleReading {
  faces: number;
  gaze: GazeStatus | null;
}
interface SampleSource {
  engine: string;
  read(video: HTMLVideoElement): Promise<SampleReading | null>;
  close(): void;
}

export interface ProctoringOptions {
  /** "strict" swaps BlazeFace for the FaceLandmarker (one detector serving face count AND gaze)
   *  and arms the gaze machine. Defaults to "natural" — existing callers are untouched. */
  mode?: InterviewMode;
  /** True exactly while the candidate is in the speaking window of an answer. The gaze machine is
   *  fed ONLY then — outside it every tick is null, so thinking time is free by construction. */
  answeringRef?: RefObject<boolean>;
}

export interface Proctoring {
  start: () => void;
  stop: () => void;
  onPaste: (e: PasteLike) => void;
  /** Strict mode swallowed a paste: the length is the hint, the text is never read. Zero penalty
   *  in the shared price list — the paste never landed, so the flag is context, not an offence. */
  onPasteBlocked: (len: number) => void;
  /** Enqueue one strict-enforcement timeline event. Silently dropped unless proctoring is active
   *  (same rule as every other enqueue) — buffer pre-start bypasses upstream and flush after
   *  start(), or they never reach the report. */
  emitStrict: (kind: StrictEventKind, detail: string) => void;
  /** Non-null only while a sustained camera-presence problem is showing. Render it inline. */
  cameraNotice: CameraNotice | null;
}

/**
 * @param cameraActive whether a camera stream is genuinely live on `videoRef`. Face presence is
 *   gated on this and nothing else — passing `true` for a camera that is denied, absent or still
 *   negotiating is how a candidate gets accused of hiding from a camera they never had.
 */
export function useProctoring(
  token: string,
  videoRef: RefObject<HTMLVideoElement | null>,
  cameraActive: boolean,
  opts?: ProctoringOptions,
): Proctoring {
  const mode: InterviewMode = opts?.mode ?? "natural";
  const queueRef = useRef<IntegrityEvent[]>([]);
  const activeRef = useRef(false);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const faceTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Transition state so we FLAG changes, not every poll — one tab_blur per switch-away, one
  // no_face per disappearance — rather than a noisy stream a recruiter would have to wade through.
  const blurredRef = useRef(false);
  const fullscreenRef = useRef(false);
  const faceBusyRef = useRef(false);
  const faceSourceRef = useRef<SampleSource | null>(null);
  const machineRef = useRef<PresenceMachine>(createPresenceMachine());
  const gazeMachineRef = useRef<GazeMachine>(createGazeMachine());
  const watchAnnouncedRef = useRef(false);
  const gazeAnnouncedRef = useRef(false);
  // Read synchronously inside the sampler, which runs on a timer that can outlive a re-render.
  const cameraActiveRef = useRef(cameraActive);
  cameraActiveRef.current = cameraActive;
  // The options object is rebuilt by the caller on every render; holding it (or answeringRef) in
  // any callback's dependency chain would recreate the sampler each render and make the loader
  // effect tear down and re-download a multi-MB detector per keystroke. The sampler reads through
  // this ref instead, synchronously, at the instant of each tick.
  const answeringExternalRef = useRef<RefObject<boolean> | undefined>(opts?.answeringRef);
  answeringExternalRef.current = opts?.answeringRef;

  // The two pieces of React state here are deliberately outside every callback's dependency
  // chain: `stop` is used as an unmount cleanup below, so if a re-render could change its identity
  // a passing face would tear down the whole proctoring session.
  const [cameraNotice, setCameraNotice] = useState<CameraNotice | null>(null);
  // Mirrors activeRef for the face-sampling effect, which has to RE-RUN when the camera comes up
  // or goes away — a ref alone cannot wake an effect.
  const [running, setRunning] = useState(false);

  const enqueue = useCallback((kind: IntegrityEventKind, detail?: string): void => {
    if (!activeRef.current) return;
    const q = queueRef.current;
    if (q.length >= MAX_QUEUE) return; // cap silently
    q.push({ kind, at: new Date().toISOString(), ...(detail !== undefined ? { detail } : {}) });
  }, []);

  const flush = useCallback((): void => {
    const q = queueRef.current;
    if (q.length === 0) return;
    const batch = q.splice(0, q.length);
    // Fire-and-forget: telemetry must never block or fail the interview. On error we drop the
    // batch (these signals are advisory) rather than retry-storm against the API.
    void api.post(`/invite/${token}/interview/integrity`, { events: batch }).catch(() => {});
  }, [token]);

  // --- discrete DOM signals (deduped to transitions) ---
  const handleVisibility = useCallback((): void => {
    if (typeof document === "undefined") return;
    if (document.visibilityState === "hidden") {
      if (!blurredRef.current) {
        blurredRef.current = true;
        enqueue("tab_blur", "visibility");
      }
    } else if (blurredRef.current) {
      blurredRef.current = false;
      enqueue("tab_focus", "visibility");
    }
  }, [enqueue]);

  const handleBlur = useCallback((): void => {
    if (!blurredRef.current) {
      blurredRef.current = true;
      enqueue("tab_blur", "window");
    }
  }, [enqueue]);

  const handleFocus = useCallback((): void => {
    if (blurredRef.current) {
      blurredRef.current = false;
      enqueue("tab_focus", "window");
    }
  }, [enqueue]);

  const handleFullscreen = useCallback((): void => {
    const isFs = typeof document !== "undefined" && document.fullscreenElement != null;
    if (fullscreenRef.current && !isFs) enqueue("fullscreen_exit");
    fullscreenRef.current = isFs;
  }, [enqueue]);

  // Attached to the answer/code textareas — the pasted length is a hint, we never read the text.
  const onPaste = useCallback(
    (e: PasteLike): void => {
      const len = e.clipboardData?.getData("text")?.length ?? 0;
      enqueue("paste", String(len));
    },
    [enqueue],
  );

  // Strict mode's swallowed paste — the interview surface calls preventDefault, then this. Same
  // rule as onPaste: the LENGTH travels, the text never does.
  const onPasteBlocked = useCallback(
    (len: number): void => {
      enqueue("paste_blocked", String(Math.max(0, Math.round(len))));
    },
    [enqueue],
  );

  const emitStrict = useCallback(
    (kind: StrictEventKind, detail: string): void => {
      enqueue(kind, detail);
    },
    [enqueue],
  );

  // --- camera presence sampling ---
  const sampleFace = useCallback(async (): Promise<void> => {
    const source = faceSourceRef.current;
    const video = videoRef.current;
    if (!source || !video || faceBusyRef.current || !activeRef.current) return;
    // The camera can go away mid-interview (the candidate revokes permission, unplugs the webcam,
    // the track ends). The effect below tears the sampler down for that, but a tick already in
    // flight must not take a reading off a video that has just gone blank.
    if (!cameraActiveRef.current) return;
    // A hidden tab freezes the video element; whatever the detector sees then is stale, and the
    // candidate already has a tab_blur on the record. Don't double-count the same moment.
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;

    faceBusyRef.current = true;
    try {
      const reading = await source.read(video);
      if (reading === null || !activeRef.current) return; // no reading — not "no face"

      // First genuine reading proves we are actually watching this candidate's camera. Only now
      // may anything downstream say camera presence was observed.
      if (!watchAnnouncedRef.current) {
        watchAnnouncedRef.current = true;
        enqueue("face_watch_on", source.engine);
      }
      // The gaze estimator's own proof-of-watching, keyed on the ENGINE rather than the mode: only
      // the landmarker estimates gaze, and without this heartbeat every gaze_away row is
      // inadmissible by the shared computePresence rule — no detector, no data, no claim.
      if (source.engine === "mediapipe-landmarker" && !gazeAnnouncedRef.current) {
        gazeAnnouncedRef.current = true;
        enqueue("gaze_watch_on", source.engine);
      }

      const { machine, emits } = observeFaces(machineRef.current, reading.faces, Date.now());
      machineRef.current = machine;
      for (const e of emits) enqueue(e.kind, e.detail);

      // Gaze is measured ONLY inside the speaking window. Outside it — Asha talking, the
      // candidate thinking, a typed answer — the machine is fed null and stays untouched, so
      // thinking time is free by construction, not by policy. reading.gaze is itself null unless
      // exactly one face was in frame, and is always null from the natural-mode detector.
      const answering = answeringExternalRef.current?.current === true;
      const gazeStep = observeGaze(gazeMachineRef.current, answering ? reading.gaze : null, Date.now());
      gazeMachineRef.current = gazeStep.machine;
      for (const e of gazeStep.emits) enqueue(e.kind, e.detail);

      setCameraNotice((prev) => {
        const next = noticeFor(machine.confirmed);
        // Keep the identity stable while the status is unchanged — no pointless re-renders of the
        // interview surface mid-answer.
        return prev?.status === next?.status ? prev : next;
      });
    } finally {
      faceBusyRef.current = false;
    }
  }, [enqueue, videoRef]);

  const start = useCallback((): void => {
    if (activeRef.current) return;
    activeRef.current = true;
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibility);
      document.addEventListener("fullscreenchange", handleFullscreen);
      fullscreenRef.current = document.fullscreenElement != null;
    }
    if (typeof window !== "undefined") {
      window.addEventListener("blur", handleBlur);
      window.addEventListener("focus", handleFocus);
      window.addEventListener("pagehide", flush); // best-effort flush if the tab is closed mid-interview
    }
    flushTimerRef.current = setInterval(flush, FLUSH_MS);
    // The heartbeat that makes an empty event list mean "nothing happened" instead of "we have no
    // idea". Without it the scorer cannot tell a clean session from an unreported one.
    enqueue("proctor_on");

    machineRef.current = createPresenceMachine(Date.now());
    gazeMachineRef.current = createGazeMachine(Date.now());
    watchAnnouncedRef.current = false;
    gazeAnnouncedRef.current = false;
    // Face presence is NOT started here. It waits for a live camera — see the effect below.
    setRunning(true);
  }, [handleVisibility, handleFullscreen, handleBlur, handleFocus, flush, enqueue]);

  /**
   * Camera presence, gated on a camera that genuinely exists.
   *
   * This used to hang off `start()`, which fires when the INTERVIEW goes live — so a candidate who
   * denied the camera still downloaded the detector and still got sampled against an empty <video>
   * once a second. Now the detector is only fetched when proctoring is running AND a stream is up,
   * and it is torn down the moment either stops. Nothing is downloaded, nothing is read and
   * nothing is emitted for a candidate without a camera.
   */
  useEffect(() => {
    if (!running || !cameraActive) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    // Detector setup is async (a self-hosted model has to load). Nothing waits on it, and if it
    // never resolves a detector the interview is exactly as it was: no events, no notice.
    //
    // STRICT loads the FaceLandmarker INSTEAD of BlazeFace — one detector serving both the face
    // count (into the unchanged presence machine) and the gaze estimate. Never both: two wasm
    // heaps plus two models mid-interview is a stutter the candidate would feel. Natural sessions
    // keep the exact detector they always had, wrapped to the common reading shape.
    const wrapFaceSource = (): Promise<SampleSource | null> =>
      createFaceSource().then((s) =>
        s
          ? {
              engine: s.engine,
              read: async (video: HTMLVideoElement): Promise<SampleReading | null> => {
                const faces = await s.read(video);
                return faces === null ? null : { faces, gaze: null };
              },
              close: () => s.close(),
            }
          : null,
      );
    // A metered/save-data client refuses the landmarker download (shouldLoadLocalModel) — but the
    // browser's free native FaceDetector, where one exists, costs zero bytes. Falling back keeps
    // camera PRESENCE measured on exactly the clients natural mode could measure; only the gaze
    // estimate is honestly absent. Strict must never yield LESS presence data than natural would.
    const load: Promise<SampleSource | null> =
      mode === "strict"
        ? createGazeFaceSource().then((g) => g ?? wrapFaceSource())
        : wrapFaceSource();
    void load.then((source) => {
      if (!source) return;
      if (cancelled || !activeRef.current) {
        source.close(); // stopped, or the camera went away, while we were loading
        return;
      }
      faceSourceRef.current = source;
      timer = setInterval(() => {
        void sampleFace();
      }, FACE_SAMPLE_MS);
      faceTimerRef.current = timer;
    });

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      if (faceTimerRef.current === timer) faceTimerRef.current = null;
      faceSourceRef.current?.close();
      faceSourceRef.current = null;
      // We have stopped watching, so anything either machine still believes is unverifiable from
      // here: no return receipt is owed (we did not see them come back) and the run must not be
      // stitched onto whatever we see if the camera returns. Both machines reset together — a
      // gaze episode cannot outlive the camera that observed it.
      machineRef.current = createPresenceMachine(Date.now());
      gazeMachineRef.current = createGazeMachine(Date.now());
      // …and the notice goes with it. Asking someone to fix their framing when we can no longer
      // see them would be nagging about nothing.
      setCameraNotice(null);
    };
  }, [running, cameraActive, mode, sampleFace]);

  const stop = useCallback((): void => {
    if (!activeRef.current) return;
    activeRef.current = false;
    setRunning(false); // tears the face-sampling effect down through its cleanup
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", handleVisibility);
      document.removeEventListener("fullscreenchange", handleFullscreen);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("pagehide", flush);
    }
    if (flushTimerRef.current) {
      clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    if (faceTimerRef.current) {
      clearInterval(faceTimerRef.current);
      faceTimerRef.current = null;
    }
    faceSourceRef.current?.close();
    faceSourceRef.current = null;
    // An episode still open at the end — presence or gaze — gets no return receipt: we report
    // what we saw, and we did not see them come back. The notice goes, though: the interview is
    // over, there is nothing left for the candidate to fix.
    gazeMachineRef.current = createGazeMachine(Date.now());
    setCameraNotice(null);
    flush(); // ship whatever's left before we tear down
  }, [handleVisibility, handleFullscreen, handleBlur, handleFocus, flush]);

  // Safety net: if the interview screen unmounts while still capturing, flush + detach listeners.
  useEffect(() => stop, [stop]);

  return useMemo(
    () => ({ start, stop, onPaste, onPasteBlocked, emitStrict, cameraNotice }),
    [start, stop, onPaste, onPasteBlocked, emitStrict, cameraNotice],
  );
}
